// 홈 진열 섹션 관리(/emp/home/sections) 화면들이 공유하는 상수·헬퍼입니다.
//
// 이 기능의 API는 관리자 API 기본값인 snake_case 를 씁니다.
// (camelCase 인 것은 /emp/legal 계열 뿐이고, 그건 docs/인증_약관동의.md 에 예외로 명시돼 있습니다)
// 진열 아이템 응답이 교재 목록 API 의 필드(barcode·cover_image_url·publisher)를 그대로
// 실어 나르기 때문에, 표기를 바꾸면 도서 선택 모달과 같은 카드를 두 벌 만들어야 합니다.

// 섹션 타이틀 입력 언어. src/lang/lang.js 가 지원하는 5개 언어와 동일하게 유지합니다.
export const HOME_LANGS = [
  { code: "ko", label: "한국어" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
];

// 앱 홈에서 이 섹션의 도서 목록을 어떤 형태로 펼칠지.
// 값·라벨은 디자인 시안(docs/파인애플 홈 진열 섹션.html)의 layout 4종과 1:1 로 맞췄습니다.
// 'more' 만 시안의 'cover' 와 이름이 다른데, 이미 배포된 백엔드 enum 값이라 그대로 둡니다.
// (시안의 cover = 커버 그리드 + 더보기 = 기존 more 와 동작이 같습니다)
export const DISPLAY_TYPES = [
  {
    value: "list",
    label: "세로 리스트",
    icon: "view_list",
    desc: "가로로 넓은 행 카드가 세로로 쌓입니다. 태블릿에서는 2열로 배치됩니다.",
  },
  {
    value: "carousel",
    label: "가로 스크롤",
    icon: "view_carousel",
    desc: "커버 카드가 한 줄로 이어지고 옆으로 밀어서 봅니다. 노출 개수만큼만 실립니다.",
  },
  {
    value: "carousel2",
    label: "가로 2행",
    icon: "splitscreen",
    desc: "카드가 2행으로 쌓인 채 옆으로 밀립니다. 회색 밴드 배경에 페이지 점이 붙습니다.",
  },
  {
    value: "more",
    label: "커버 그리드",
    icon: "grid_view",
    desc: "커버를 그리드로 깔고(모바일 2열·태블릿 5열), 아래 '더보기'로 전체 목록 화면으로 넘깁니다.",
  },
];

// 가로로 밀어서 보는 방식. 미리보기의 스크롤 안내와 앱 동작 설명이 같은 판정을 씁니다.
export const isScrollType = (v) => v === "carousel" || v === "carousel2";

// 교재 상태. EditTextbook.vue 의 statusLabel 과 같은 값을 씁니다.
// 앱 공개 조건은 status='approval' 이면서 버전 1.0 이상이라, status 만으로는 판정할 수 없습니다.
// (docs/교재_시리즈_프론트가이드.md:234) 그래서 최종 판정은 서버가 준 is_public 을 우선합니다.
// 미요청은 두 형태로 옵니다 — 한 번도 요청한 적 없으면 빈 값,
// 요청을 냈다가 취소했으면 문자열 'none'(가이드 §3의 허용값). 라벨은 같아야 합니다.
export const TEXTBOOK_STATUS = {
  approval: { label: "승인완료", pill: "ok" },
  request: { label: "승인요청", pill: "warn" },
  rejection: { label: "거절", pill: "err" },
  none: { label: "미요청", pill: "default" },
  "": { label: "미요청", pill: "default" },
};

const labelOf = (list, value) => list.find((x) => x.value === value)?.label || value || "-";

export const displayTypeLabel = (v) => labelOf(DISPLAY_TYPES, v);
export const displayTypeIcon = (v) =>
  DISPLAY_TYPES.find((x) => x.value === v)?.icon || "view_carousel";
export const langLabel = (code) => HOME_LANGS.find((l) => l.code === code)?.label || code;

export const textbookStatusLabel = (s) => (TEXTBOOK_STATUS[s || ""] || {}).label || s;
export const textbookStatusPill = (s) => (TEXTBOOK_STATUS[s || ""] || {}).pill || "default";

/**
 * 이 아이템이 앱에서 실제로 보이는지 판정합니다.
 * 서버가 is_public 을 주면 그걸 그대로 믿고, 없으면 status 로만 어림합니다.
 * (status='approval' 이어도 버전이 1.0 미만이면 앱에 안 나오므로 어림값은 낙관적입니다)
 * @param {Object} item SectionItem
 * @returns {boolean}
 */
export const isItemPublic = (item = {}) => {
  if (typeof item.is_public === "boolean") return item.is_public;
  return item.status === "approval";
};

/**
 * 섹션 타이틀 맵에서 표시할 문자열 하나를 고릅니다. 없으면 ko → 아무 값 → section_key 순으로 폴백.
 * @param {Object} section
 * @param {string} lang
 * @returns {string}
 */
export const pickTitle = (section = {}, lang = "ko") => {
  const titles = section.titles || {};
  if (titles[lang]) return titles[lang];
  if (titles.ko) return titles.ko;
  const first = Object.keys(titles).find((k) => titles[k]);
  return first ? titles[first] : section.section_key || "-";
};

// 표지 이미지 폴백.
// 기존 화면들이 쓰는 /img/default-cover.png 는 public/ 에 실제로 없어서 @error 로 때우고 있습니다.
// 신규 화면은 네트워크를 타지 않는 인라인 SVG 만 씁니다.
export const COVER_FALLBACK =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="84" viewBox="0 0 60 84">' +
      '<rect width="60" height="84" rx="4" fill="#F0E8D2"/>' +
      '<path d="M20 30h20v3H20zm0 8h20v3H20zm0 8h13v3H20z" fill="#C9C1AF"/>' +
      "</svg>"
  );

/**
 * <img @error> 핸들러. 깨진 표지를 폴백 SVG 로 바꾸고 재귀 호출을 끊습니다.
 * @param {Event} e
 */
export const onCoverError = (e) => {
  const el = e?.target;
  if (!el || el.dataset.fallback === "1") return;
  el.dataset.fallback = "1";
  el.src = COVER_FALLBACK;
};

/**
 * 가격 표시. 교재에는 가격이 없고 서버가 상품에서 뽑아준 대표가만 옵니다. 없으면 "—".
 * @param {number|null} price
 * @param {string} currency
 * @returns {string}
 */
export const fmtPrice = (price, currency = "KRW") => {
  if (price === null || price === undefined || price === "") return "—";
  const n = Number(price);
  if (Number.isNaN(n)) return "—";
  if (currency === "KRW") return `${n.toLocaleString()}원`;
  return `${currency} ${n.toLocaleString()}`;
};

/**
 * 섹션이 앱에 제대로 안 나올 만한 이유를 모읍니다. 목록 상단 배너와 행 뱃지가 같은 판정을 씁니다.
 * @param {Object} section
 * @returns {string[]} 비어 있으면 정상
 */
export const sectionWarnings = (section = {}) => {
  const out = [];
  if (!section.item_count) out.push("진열된 도서가 없습니다");
  else if (section.unpublished_count) out.push(`미공개 교재 ${section.unpublished_count}권`);
  if (section.active_yn !== "Y") out.push("노출이 꺼져 있습니다");
  return out;
};

/**
 * 배열에서 index 항목을 위(-1)/아래(+1)로 한 칸 옮긴 새 배열을 돌려줍니다.
 * 원본을 건드리지 않으므로 되돌리기 스냅샷과 함께 쓰기 좋습니다.
 * @param {Array} list
 * @param {number} index
 * @param {number} dir -1 이면 위, 1 이면 아래
 * @returns {Array}
 */
export const moveItem = (list, index, dir) => {
  const next = index + dir;
  if (index < 0 || next < 0 || index >= list.length || next >= list.length) return list;
  const out = [...list];
  [out[index], out[next]] = [out[next], out[index]];
  return out;
};

/**
 * 순서 일괄 저장 payload 를 만듭니다. sort_order 는 화면에 보이는 순서 그대로 1부터 붙입니다.
 * @param {Array} list 화면 순서대로 정렬된 배열
 * @param {string} idKey "idx" (섹션) 또는 "item_idx" (아이템)
 * @returns {Array<{sort_order: number}>}
 */
export const orderPayload = (list = [], idKey = "idx") =>
  list.map((row, i) => ({ [idKey]: row[idKey], sort_order: i + 1 }));

/**
 * 두 배열의 순서가 같은지 비교합니다. "순서 저장" 버튼 노출 판정에 씁니다.
 * @param {Array} a
 * @param {Array} b
 * @param {string} idKey
 * @returns {boolean}
 */
export const sameOrder = (a = [], b = [], idKey = "idx") =>
  a.length === b.length && a.every((row, i) => row[idKey] === b[i][idKey]);

/**
 * 서버가 준 섹션을 화면이 기대하는 형태로 채웁니다. (필드 누락 방어)
 * @param {Object} s
 * @returns {Object}
 */
export const normalizeSection = (s = {}) => ({
  ...s,
  titles: s.titles || {},
  display_type: s.display_type || "carousel",
  display_limit: Number(s.display_limit ?? 10),
  active_yn: s.active_yn === "Y" ? "Y" : "N",
  sort_order: Number(s.sort_order || 0),
  item_count: Number(s.item_count || 0),
  unpublished_count: Number(s.unpublished_count || 0),
});
